import type { ComponentProps, CSSProperties, ReactNode, Ref } from 'react';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  IconBadge,
} from '@monksflow/monks-ui';
import { twMerge } from 'tailwind-merge';

import { EmptyStateTitle } from '../EmptyStateTitle';

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /**
   * Rendered in a muted `IconBadge` above the title, sized from `size`. Omit it for the
   * iconless treatment.
   */
  icon?: ReactNode;
  /**
   * A badge of the host's own to stand above the title, in place of the standard one - for a
   * screen whose empty state carries a different size or treatment. Outranks `icon`, and is
   * rendered as given: the surrounding box is the media slot's spacing and nothing else.
   */
  media?: ReactNode;
  /** Action slot, usually a single primary button. */
  children?: ReactNode;
  /** Border treatment, straight from the design system's `Empty`. */
  variant?: ComponentProps<typeof Empty>['variant'];
  /**
   * `compact` for an empty state living in a side panel rather than on a page: the design
   * system's own title step in place of the larger house one, no wide-viewport padding step,
   * which a fixed-width panel has no room to spend however wide the window gets, and the
   * badge one size down, so an absence does not out-shout the content beside it.
   */
  size?: 'default' | 'compact';
  /**
   * Caps the description's line length. The design system leaves it unmeasured, so a
   * long subtitle runs the full width of its host, and `EmptyDescription` is not
   * reachable from this component's `className`.
   */
  descriptionMaxWidth?: CSSProperties['maxWidth'];
  className?: string;
  /**
   * Height is the only dimension hosts ever set from the outside, so it gets a
   * prop of its own. The component is self-sizing, so nothing is applied unless
   * a caller asks.
   */
  height?: CSSProperties['height'];
  /** Escape hatch for the rest of the box; a `height` here outranks the prop. */
  style?: CSSProperties;
  /** `alert` for states that represent a failure rather than an absence of data. */
  role?: 'status' | 'alert';
  'data-testid'?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * The one empty state. Composes the monks-ui `Empty` primitives so every
 * screen reads the same, replacing the MUI-era bordered card and the
 * hand-rolled Tailwind sibling that preceded it.
 *
 * Iconless and borderless by default, per Figma node 5838:171998.
 */
export function EmptyState({
  title,
  description,
  icon,
  media,
  children,
  variant,
  size = 'default',
  descriptionMaxWidth,
  className,
  height,
  style,
  role = 'status',
  'data-testid': testId,
  ref,
}: EmptyStateProps) {
  const boxStyle = height === undefined ? style : { height, ...style };
  const isCompact = size === 'compact';
  // `media` and `size='compact'` are the only opt-ins into the restyled spacing and media
  // treatment; without either, this must render exactly what origin/main does.
  const usesNewTreatment = isCompact || media !== undefined;
  const rootClassName = usesNewTreatment
    ? twMerge(isCompact ? 'gap-sm p-md md:p-md' : 'gap-md', className)
    : className;

  return (
    <Empty
      ref={ref}
      role={role}
      data-testid={testId}
      variant={variant}
      className={rootClassName}
      style={boxStyle}
    >
      {usesNewTreatment ? (
        (media || icon) && (
          // The media slot carries a bottom margin of its own on top of the container's gap, so
          // the badge sat about twice as far from the title as the title does from its
          // description. The gap is the one that spaces every child, so the margin goes.
          <EmptyMedia data-testid="empty-state-media" className="mb-0">
            {media ?? (
              <IconBadge
                data-testid="empty-state-icon"
                variant="muted"
                size={isCompact ? 'sm' : 'default'}
              >
                {icon}
              </IconBadge>
            )}
          </EmptyMedia>
        )
      ) : icon ? (
        <EmptyMedia variant="icon">{icon}</EmptyMedia>
      ) : null}
      <EmptyHeader>
        <EmptyStateTitle className={isCompact ? 'text-base font-medium' : undefined}>
          {title}
        </EmptyStateTitle>
        {description ? (
          <EmptyDescription
            style={
              descriptionMaxWidth === undefined ? undefined : { maxWidth: descriptionMaxWidth }
            }
          >
            {description}
          </EmptyDescription>
        ) : null}
      </EmptyHeader>
      {children ? <EmptyContent>{children}</EmptyContent> : null}
    </Empty>
  );
}

EmptyState.displayName = 'EmptyState';
